"use client";

import { useEffect, useRef, useState } from "react";
import { useMutation } from "convex/react";
import { api } from "convex/_generated/api";
import type { Id } from "convex/_generated/dataModel";
import { ArrowDown, ArrowUp, ImagePlus, Loader2, RefreshCw, Trash2, Upload } from "lucide-react";
import { AUDIO_IMAGE_MAX_BYTES, AUDIO_MAX_IMAGES } from "@/../shared/audio";
import { cn } from "@/lib/utils";

export type AudioUploadPage = {
  id: string;
  file: File;
  previewUrl: string;
  progress: number;
  storageId?: Id<"_storage">;
  error?: string;
};

type Props = {
  pages: AudioUploadPage[];
  onChange: (pages: AudioUploadPage[]) => void;
  onUploaded: (fileCount: number) => void;
};

const ACCEPTED_TYPES = new Set(["image/jpeg", "image/png"]);

function fingerprint(file: File) {
  return `${file.name}:${file.size}:${file.lastModified}`;
}

export function AudioPageUploader({ pages, onChange, onUploaded }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const pagesRef = useRef(pages);
  useEffect(() => { pagesRef.current = pages; }, [pages]);
  const generateUploadUrl = useMutation(api.files.generateUploadUrl);
  const deleteUploadedFiles = useMutation(api.files.deleteUploadedFiles);
  const [dragging, setDragging] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const publish = (next: AudioUploadPage[]) => {
    pagesRef.current = next;
    onChange(next);
  };

  const upload = async (page: AudioUploadPage) => {
    try {
      const uploadUrl = await generateUploadUrl();
      const storageId = await new Promise<Id<"_storage">>((resolve, reject) => {
        const request = new XMLHttpRequest();
        request.open("POST", uploadUrl);
        request.setRequestHeader("Content-Type", page.file.type);
        request.upload.onprogress = (event) => {
          if (!event.lengthComputable) return;
          const progress = Math.max(1, Math.round((event.loaded / event.total) * 100));
          publish(pagesRef.current.map((item) => item.id === page.id ? { ...item, progress } : item));
        };
        request.onerror = () => reject(new Error("upload_failed"));
        request.onload = () => {
          if (request.status < 200 || request.status >= 300) return reject(new Error("upload_failed"));
          try {
            resolve((JSON.parse(request.responseText) as { storageId: Id<"_storage"> }).storageId);
          } catch {
            reject(new Error("upload_failed"));
          }
        };
        request.send(page.file);
      });
      publish(pagesRef.current.map((item) => item.id === page.id ? { ...item, progress: 100, storageId, error: undefined } : item));
      onUploaded(1);
    } catch {
      publish(pagesRef.current.map((item) => item.id === page.id ? { ...item, progress: 0, error: "Upload failed. Check your connection and try again." } : item));
    }
  };

  const addFiles = (files: File[]) => {
    setNotice(null);
    const remaining = AUDIO_MAX_IMAGES - pagesRef.current.length;
    const existing = new Set(pagesRef.current.map((page) => fingerprint(page.file)));
    if (remaining <= 0) {
      setNotice(`You can upload up to ${AUDIO_MAX_IMAGES} textbook pages.`);
      return;
    }
    if (files.length > remaining) {
      setNotice(`Only the first ${remaining} ${remaining === 1 ? "page" : "pages"} were added. The limit is ${AUDIO_MAX_IMAGES}.`);
    } else if (files.some((file) => existing.has(fingerprint(file)))) {
      setNotice("A duplicate image was skipped.");
    }
    const next = files.slice(0, Math.max(0, remaining)).flatMap<AudioUploadPage>((file) => {
      if (existing.has(fingerprint(file))) return [];
      const id = crypto.randomUUID();
      const base = { id, file, previewUrl: URL.createObjectURL(file), progress: 0 };
      if (!ACCEPTED_TYPES.has(file.type)) return [{ ...base, error: "Use a JPG or PNG image." }];
      if (file.size > AUDIO_IMAGE_MAX_BYTES) return [{ ...base, error: "This image is larger than 3 MB." }];
      return [base];
    });
    if (next.length === 0) return;
    publish([...pagesRef.current, ...next]);
    for (const page of next) if (!page.error) void upload(page);
  };

  const remove = (id: string) => {
    const page = pagesRef.current.find((item) => item.id === id);
    if (page) URL.revokeObjectURL(page.previewUrl);
    if (page?.storageId) {
      void deleteUploadedFiles({ lessonImageIds: [page.storageId] });
    }
    publish(pagesRef.current.filter((item) => item.id !== id));
  };

  const move = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= pagesRef.current.length) return;
    const next = [...pagesRef.current];
    [next[index], next[target]] = [next[target], next[index]];
    publish(next);
  };

  return (
    <div className="space-y-3">
      <div
        className={cn("rounded-xl border border-dashed px-5 py-7 text-center transition-colors", dragging ? "border-emerald-600 bg-emerald-50" : "border-slate-300 bg-slate-50")}
        onDragEnter={(event) => { event.preventDefault(); setDragging(true); }}
        onDragOver={(event) => event.preventDefault()}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => { event.preventDefault(); setDragging(false); addFiles(Array.from(event.dataTransfer.files)); }}
      >
        <Upload className="mx-auto h-6 w-6 text-emerald-700" aria-hidden="true" />
        <p className="mt-2 text-sm font-semibold text-slate-900">Upload textbook pages</p>
        <p className="mt-1 text-sm text-slate-600">Use clear, well-lit photos of the dialogue or reading passage.</p>
        <button type="button" onClick={() => inputRef.current?.click()} className="mt-4 min-h-11 rounded-lg border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-800 shadow-sm hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600">
          Choose images
        </button>
        <input ref={inputRef} type="file" accept="image/jpeg,image/png" multiple className="sr-only" onChange={(event) => { addFiles(Array.from(event.target.files ?? [])); event.currentTarget.value = ""; }} />
        <p className="mt-2 text-xs text-slate-500">JPG or PNG · up to {AUDIO_MAX_IMAGES} pages · 3 MB each</p>
      </div>

      {pages.length > 0 && (
        <ul className="grid gap-2" aria-label="Textbook pages in reading order">
          {pages.map((page, index) => (
            <li key={page.id} className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white p-2.5">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src={page.previewUrl} alt={`Textbook page ${index + 1}`} className="h-14 w-14 rounded-md object-cover" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-slate-800">Page {index + 1}</p>
                {page.error ? <p className="text-xs text-red-700" role="alert">{page.error}</p> : page.storageId ? <p className="text-xs text-emerald-700">Ready</p> : <div className="mt-1" aria-label={`Uploading page ${index + 1}: ${page.progress}%`}><div className="h-1.5 overflow-hidden rounded-full bg-slate-200"><div className="h-full bg-emerald-600" style={{ width: `${page.progress}%` }} /></div></div>}
              </div>
              <div className="flex items-center gap-1">
                {page.error && <button type="button" onClick={() => void upload({ ...page, error: undefined })} aria-label={`Retry page ${index + 1}`} className="grid size-10 place-items-center rounded-lg text-slate-600 hover:bg-slate-100"><RefreshCw className="size-4" /></button>}
                {!page.storageId && !page.error && <Loader2 className="mx-3 size-4 animate-spin text-emerald-700 motion-reduce:animate-none" aria-hidden="true" />}
                <button type="button" disabled={index === 0} onClick={() => move(index, -1)} aria-label={`Move page ${index + 1} earlier`} className="grid size-10 place-items-center rounded-lg text-slate-600 hover:bg-slate-100 disabled:opacity-30"><ArrowUp className="size-4" /></button>
                <button type="button" disabled={index === pages.length - 1} onClick={() => move(index, 1)} aria-label={`Move page ${index + 1} later`} className="grid size-10 place-items-center rounded-lg text-slate-600 hover:bg-slate-100 disabled:opacity-30"><ArrowDown className="size-4" /></button>
                <button type="button" onClick={() => remove(page.id)} aria-label={`Remove page ${index + 1}`} className="grid size-10 place-items-center rounded-lg text-red-700 hover:bg-red-50"><Trash2 className="size-4" /></button>
              </div>
            </li>
          ))}
        </ul>
      )}
      {pages.length === 0 && <p className="flex items-center gap-2 text-xs text-slate-500"><ImagePlus className="size-4" aria-hidden="true" /> You can add more than one page and arrange the reading order.</p>}
      {notice ? <p role="status" className="text-xs text-amber-800">{notice}</p> : null}
    </div>
  );
}
